import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Alert, Appearance, TextStyle, View, ViewStyle } from "react-native"
import { Button, LoginForm, Screen, Text } from "../../../components"
import { useStores } from "../../../models"
import { color } from "../../../theme"
import { AppStackInitialSync, navigate, resetRoot } from "../../../navigators"
import { FormikProps, FormikValues } from "formik"
import { translate } from "../../../i18n"
import JalaAnalytic from "../../../utils/jala-analytic"
// import { AppStack } from "../../../navigators"


const ROOT: ViewStyle = {
  flexGrow: 1
}

const HEADER_TEXT: TextStyle = {
  marginTop: 16,
  marginBottom: 12,
  marginHorizontal: 16,
}

const LOGIN_TEXT: TextStyle = {
  marginBottom: 16,
  marginHorizontal: 16,
}

const FORGOT: ViewStyle = {
  alignSelf: 'flex-end',
  marginHorizontal: 16,
}

const SPACE: ViewStyle = {
  flex: 1,
}

const BUTTON: ViewStyle = {
  marginTop: 30,
  marginBottom: 16,
  marginHorizontal: 16,
}

export const LoginScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function LoginScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  // Pull in one of our MST stores
  const {
    tokenStore, tokenStore: { loading: tokenLoading, token, error: tokenError },
  } = useStores()
  const [ isError, setIsError ] = useState<boolean>(true)

  const loginForm = useRef<FormikProps<FormikValues>>()
  const loginFormRef = useCallback((node) => {
    loginForm.current = node;
    if (loginForm.current) setIsError(!(loginForm.current.dirty && loginForm.current.isValid))
  }, [])

  useEffect(() => {
    tokenStore.setLoading(false)
    tokenStore.setError(null)
  }, [tokenStore])

  useEffect(() => {
    if (token) resetRoot(AppStackInitialSync)
  }, [token])

  useEffect(() => {
    if (tokenError) {
      let subtitle = translate("loginScreen.failedAlertSubtitle")
      if(tokenError?.kind === 'cannot-connect') subtitle = "Network Error"
      Alert.alert(
        translate("loginScreen.failedAlert"),
        subtitle,
        [ { onPress: () => tokenStore.setError(null) } ]
      );
    }
    return () => tokenStore.setError(null)
  }, [tokenError, tokenStore])

  const onSubmit = useCallback(({ username, password }) => {
    if (tokenLoading) return;
    tokenStore.requestToken({ username, password })
      .then((response) => {
        JalaAnalytic.log('Mobile App: login count');
        JalaAnalytic.login();
        return response;
      })
  }, [tokenLoading, tokenStore])

  const handleSubmit = useCallback((event) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (loginForm.current) {
      const inputValue = loginForm.current.values.username
      const isEmail = emailPattern.test(inputValue);
      if (isEmail) JalaAnalytic.log('Mobile App : Log in via email ');
      else JalaAnalytic.log('Mobile App : Log in via phone number');
      loginForm.current.handleSubmit(event)
    }
  }, [])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
    >
      <Text preset="display2" tx="loginScreen.login" colorScheme={colorScheme} color={colorScheme === "light" ? color.palette.blue700 : undefined} style={HEADER_TEXT} />
      <Text preset="body2" tx="loginScreen.loginSubtitle" colorScheme={colorScheme} color={colorScheme === "light" ? color.palette.gray600 : undefined} style={LOGIN_TEXT} />
      <LoginForm colorScheme={colorScheme} onSubmit={onSubmit} innerRef={loginFormRef} />
      <Button
        testID="jala:id/forgot-password-button"
        tx="loginScreen.forgotPassword"
        preset="link"
        colorScheme={colorScheme}
        textPreset="title4"
        style={FORGOT}
        onPress={() => navigate('Login.Stack', 'Login.PasswordReset')}
      />
      <View style={SPACE} />
      <Button
        testID="jala:id/login-button"
        tx="loginScreen.login"
        colorScheme={colorScheme}
        style={BUTTON}
        disabled={isError}
        loading={tokenLoading}
        onPress={handleSubmit}
      />
    </Screen>
  )
})
